import React, { useEffect, useState } from "react";
import { Package, ShoppingBag, DollarSign, Boxes, Plus, Pencil, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const emptyForm = { name: "", price: "", category: "graphic", image: "", description: "", colors: "", sizes: "", badge: "" };

function ProductForm({ open, onClose, initial, onSaved }) {
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const editing = !!initial;

  useEffect(() => {
    if (initial) {
      setForm({
        name: initial.name, price: initial.price, category: initial.category, image: initial.image,
        description: initial.description || "", colors: (initial.colors || []).join(", "),
        sizes: (initial.sizes || []).join(", "), badge: initial.badge || "",
      });
    } else setForm(emptyForm);
  }, [initial, open]);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      name: form.name, price: parseFloat(form.price), category: form.category, image: form.image,
      description: form.description,
      colors: form.colors.split(",").map((s) => s.trim()).filter(Boolean),
      sizes: form.sizes.split(",").map((s) => s.trim()).filter(Boolean),
      badge: form.badge || null,
    };
    try {
      if (editing) await api.put(`/admin/products/${initial.product_id}`, payload);
      else await api.post("/admin/products", payload);
      toast.success(editing ? "Product updated" : "Product created");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const field = "w-full border-2 border-ink rounded-lg px-3 py-2 font-semibold outline-none focus:ring-0";

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="border-2 border-ink rounded-2xl max-w-lg bg-paper max-h-[85vh] overflow-y-auto" data-testid="product-form-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">{editing ? "Edit product" : "New product"}</DialogTitle>
          <DialogDescription className="font-semibold text-ink/60">Fill in the tee details. Colors and sizes are comma-separated.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <input required placeholder="Name" value={form.name} onChange={upd("name")} className={field} data-testid="pf-name" />
          <div className="grid grid-cols-2 gap-3">
            <input required type="number" step="0.01" placeholder="Price" value={form.price} onChange={upd("price")} className={field} data-testid="pf-price" />
            <select value={form.category} onChange={upd("category")} className={field} data-testid="pf-category">
              <option value="graphic">graphic</option>
              <option value="essentials">essentials</option>
            </select>
          </div>
          <input required placeholder="Image URL" value={form.image} onChange={upd("image")} className={field} data-testid="pf-image" />
          <textarea placeholder="Description" value={form.description} onChange={upd("description")} rows={2} className={field} data-testid="pf-description" />
          <input placeholder="Colors (comma separated)" value={form.colors} onChange={upd("colors")} className={field} data-testid="pf-colors" />
          <input placeholder="Sizes (comma separated)" value={form.sizes} onChange={upd("sizes")} className={field} data-testid="pf-sizes" />
          <input placeholder="Badge (optional: New, Bestseller, Limited)" value={form.badge} onChange={upd("badge")} className={field} data-testid="pf-badge" />
          <button type="submit" disabled={saving} className="w-full bg-ink text-paper font-bold px-6 py-3 rounded-xl neo-press border-2 border-ink disabled:opacity-60" data-testid="pf-submit">
            {saving ? "Saving…" : editing ? "Update product" : "Create product"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => {
    api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/admin/orders").then(({ data }) => setOrders(data)).catch(() => {});
    api.get("/products").then(({ data }) => setProducts(data)).catch(() => {});
  };
  useEffect(load, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try {
      await api.delete(`/admin/products/${id}`);
      toast.success("Product deleted");
      load();
    } catch { toast.error("Delete failed"); }
  };

  const statCards = stats ? [
    { icon: ShoppingBag, label: "Total Orders", value: stats.total_orders, bg: "bg-brand-yellow" },
    { icon: Package, label: "Paid Orders", value: stats.paid_orders, bg: "bg-brand-mint" },
    { icon: DollarSign, label: "Revenue", value: `$${stats.revenue.toFixed(2)}`, bg: "bg-brand-pink" },
    { icon: Boxes, label: "Products", value: stats.products, bg: "bg-brand-sky" },
  ] : [];

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12" data-testid="admin-page">
      <div className="flex items-center justify-between flex-wrap gap-4 mb-8">
        <div>
          <span className="inline-block bg-ink text-paper neo-sm rounded-full px-3 py-1 text-xs font-bold uppercase tracking-widest mb-2">Admin</span>
          <h1 className="font-display font-bold text-4xl">Dashboard</h1>
        </div>
        <button onClick={() => { setEditing(null); setFormOpen(true); }} className="inline-flex items-center gap-2 bg-ink text-paper font-bold px-5 py-3 rounded-xl neo-press border-2 border-ink" data-testid="add-product-btn">
          <Plus className="w-5 h-5" /> New product
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        {statCards.map((s) => (
          <div key={s.label} className={`${s.bg} neo rounded-2xl p-5`} data-testid={`stat-${s.label.split(" ")[0].toLowerCase()}`}>
            <s.icon className="w-6 h-6 mb-3" />
            <p className="font-display font-bold text-3xl">{s.value}</p>
            <p className="text-xs font-bold uppercase tracking-wider text-ink/60">{s.label}</p>
          </div>
        ))}
      </div>

      <Tabs defaultValue="products">
        <TabsList className="inline-flex bg-brand-yellow border-2 border-ink rounded-xl p-1 h-auto mb-6">
          <TabsTrigger value="products" data-testid="admin-tab-products" className="rounded-lg font-bold px-5 data-[state=active]:bg-ink data-[state=active]:text-paper">Products</TabsTrigger>
          <TabsTrigger value="orders" data-testid="admin-tab-orders" className="rounded-lg font-bold px-5 data-[state=active]:bg-ink data-[state=active]:text-paper">Orders</TabsTrigger>
        </TabsList>

        <TabsContent value="products">
          <div className="space-y-3">
            {products.map((p) => (
              <div key={p.product_id} className="bg-white neo rounded-2xl p-4 flex items-center gap-4" data-testid={`admin-product-${p.product_id}`}>
                <img src={p.image} alt="" className="w-16 h-16 object-cover border-2 border-ink rounded-xl shrink-0" />
                <div className="flex-1 min-w-0">
                  <h3 className="font-display font-bold text-lg leading-tight">{p.name}</h3>
                  <p className="text-xs font-bold uppercase tracking-wider text-ink/50">{p.category} · ${p.price.toFixed(2)}</p>
                </div>
                <button onClick={() => { setEditing(p); setFormOpen(true); }} className="grid place-items-center w-10 h-10 bg-brand-sky neo-sm rounded-lg neo-press" data-testid={`edit-${p.product_id}`}><Pencil className="w-4 h-4" /></button>
                <button onClick={() => remove(p.product_id)} className="grid place-items-center w-10 h-10 bg-brand-pink neo-sm rounded-lg neo-press" data-testid={`delete-${p.product_id}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="orders">
          {orders.length === 0 ? (
            <p className="font-semibold text-ink/60">No orders yet.</p>
          ) : (
            <div className="space-y-3">
              {orders.map((o) => (
                <div key={o.order_id} className="bg-white neo rounded-2xl p-4" data-testid={`admin-order-${o.order_id}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                    <div>
                      <span className="font-bold text-sm">{o.order_id}</span>
                      <span className="text-xs font-semibold text-ink/50 ml-2">{o.user_email || "guest"}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-display font-bold">${o.amount.toFixed(2)}</span>
                      <span className={`neo-sm rounded-full px-3 py-1 text-xs font-bold uppercase ${o.status === "paid" ? "bg-brand-green" : o.status === "pending" ? "bg-brand-yellow" : "bg-brand-pink"}`}>{o.status}</span>
                    </div>
                  </div>
                  <p className="text-sm font-semibold text-ink/70">{o.items.map((i) => `${i.name} ×${i.quantity}`).join(", ")}</p>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <ProductForm open={formOpen} onClose={() => setFormOpen(false)} initial={editing} onSaved={load} />
    </div>
  );
}
